import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Sparkles, Check, Loader2 } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { supabase } from "../lib/supabase";
import { SKIN_TYPES, SKIN_TONES, HAIR_TYPES, CONCERNS, GOALS } from "../data/onboarding";

const STEPS = ["Your name", "Skin type", "Skin tone", "Hair type", "Concerns", "Goals"];

export default function Onboarding() {
  const { user, profile, refreshProfile } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [data, setData] = useState({
    name: profile?.name || "",
    skin_type: profile?.skin_type || "",
    skin_tone: profile?.skin_tone || "",
    hair_type: profile?.hair_type || "",
    concerns: profile?.concerns || [],
    goals: profile?.goals || [],
  });
  const [saving, setSaving] = useState(false);

  const total = STEPS.length;

  const toggle = (key, value) => {
    setData((d) => {
      const arr = d[key] || [];
      return { ...d, [key]: arr.includes(value) ? arr.filter((x) => x !== value) : [...arr, value] };
    });
  };

  const canNext = () => {
    if (step === 0) return !!data.name.trim();
    if (step === 1) return !!data.skin_type;
    if (step === 2) return !!data.skin_tone;
    if (step === 3) return !!data.hair_type;
    if (step === 4) return data.concerns.length > 0;
    if (step === 5) return data.goals.length > 0;
    return false;
  };

  const next = () => setStep((s) => Math.min(total - 1, s + 1));
  const back = () => setStep((s) => Math.max(0, s - 1));

  const finish = async () => {
    if (!user) return;
    setSaving(true);
    // CRITICAL: Save → wait for confirmation → refetch profile → THEN navigate.
    const { error } = await supabase
      .from("users")
      .update({
        name: data.name,
        skin_type: data.skin_type,
        skin_tone: data.skin_tone,
        hair_type: data.hair_type,
        concerns: data.concerns,
        goals: data.goals,
        onboarding_complete: true,
      })
      .eq("id", user.id);
    if (error) {
      console.error("[onboarding] save error", error);
      setSaving(false);
      return;
    }
    const fresh = await refreshProfile();
    setSaving(false);
    if (fresh?.onboarding_complete) {
      // Honor the deep-link if the user arrived here via a shared link flow.
      const params = new URLSearchParams(window.location.search);
      const nextRaw = params.get("next");
      let next = null;
      if (nextRaw) {
        try {
          const decoded = decodeURIComponent(nextRaw);
          if (decoded.startsWith("/") && !decoded.startsWith("//")) next = decoded;
        } catch { /* ignore */ }
      }
      navigate(next || "/app", { replace: true });
    }
  };

  return (
    <div className="min-h-screen bg-cream">
      <div className="max-w-md mx-auto px-6 py-6">
        {/* Header bar */}
        <div className="flex items-center justify-between mb-2">
          <button
            onClick={() => (step === 0 ? navigate(-1) : back())}
            data-testid="onboarding-back"
            className="w-10 h-10 rounded-full bg-white shadow-card flex items-center justify-center"
          >
            <ArrowLeft size={18} className="text-cocoa" />
          </button>
          <span className="text-xs text-cocoa/60 font-medium tracking-wider">
            {step + 1} / {total}
          </span>
          <div className="w-10" />
        </div>

        {/* Progress */}
        <div className="h-1 bg-cocoa/10 rounded-full mt-3 mb-8 overflow-hidden">
          <motion.div
            className="h-full bg-rose"
            initial={false}
            animate={{ width: `${((step + 1) / total) * 100}%` }}
            transition={{ duration: 0.4 }}
          />
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, x: 12 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -12 }}
            transition={{ duration: 0.3 }}
          >
            {step === 0 && <StepName data={data} setData={setData} />}
            {step === 1 && <StepSkinType data={data} setData={setData} />}
            {step === 2 && <StepSkinTone data={data} setData={setData} />}
            {step === 3 && <StepHairType data={data} setData={setData} />}
            {step === 4 && <StepConcerns data={data} toggle={toggle} />}
            {step === 5 && <StepGoals data={data} toggle={toggle} />}
          </motion.div>
        </AnimatePresence>

        <div className="mt-8">
          {step < total - 1 ? (
            <button
              onClick={next}
              disabled={!canNext()}
              data-testid="onboarding-next"
              className="w-full rounded-full bg-rose hover:bg-rose-dark text-white py-3.5 font-medium transition-all shadow-rose flex items-center justify-center gap-2 disabled:opacity-40"
            >
              Continue
              <ArrowRight size={18} />
            </button>
          ) : (
            <button
              onClick={finish}
              disabled={!canNext() || saving}
              data-testid="onboarding-finish"
              className="w-full rounded-full bg-rose hover:bg-rose-dark text-white py-3.5 font-medium transition-all shadow-rose flex items-center justify-center gap-2 disabled:opacity-40"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Begin my glow <Sparkles size={16} /></>}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function StepName({ data, setData }) {
  return (
    <div data-testid="step-name">
      <p className="text-xs uppercase tracking-[0.2em] text-gold font-medium mb-2">Welcome</p>
      <h1 className="font-serif text-4xl text-cocoa mb-2">What should we call you?</h1>
      <p className="text-cocoa/60 font-light mb-7">
        Welcome to Glow &amp; Grow — let's build your personalized beauty experience.
      </p>
      <input
        autoFocus
        type="text"
        placeholder="Your first name"
        value={data.name}
        onChange={(e) => setData((d) => ({ ...d, name: e.target.value }))}
        data-testid="onboarding-name-input"
        className="w-full rounded-full bg-white border border-cocoa/10 px-6 py-4 text-base placeholder:text-cocoa/40 focus:outline-none focus:ring-2 focus:ring-rose/40"
      />
    </div>
  );
}

function StepSkinType({ data, setData }) {
  return (
    <div data-testid="step-skin-type">
      <p className="text-xs uppercase tracking-[0.2em] text-gold font-medium mb-2">About your skin</p>
      <h1 className="font-serif text-4xl text-cocoa mb-7">What's your skin type?</h1>
      <div className="space-y-2.5">
        {SKIN_TYPES.map((s) => (
          <button
            key={s.value}
            onClick={() => setData((d) => ({ ...d, skin_type: s.value }))}
            data-testid={`skin-type-${s.value.toLowerCase().replace(/\s+/g, "-")}`}
            className={`w-full flex items-center gap-4 rounded-[20px] px-5 py-4 text-left transition-all ${
              data.skin_type === s.value
                ? "bg-rose text-white shadow-rose"
                : "bg-white text-cocoa shadow-card hover:bg-rose/5"
            }`}
          >
            <span className="flex-1 font-medium">{s.value}</span>
            {data.skin_type === s.value && <Check size={18} />}
          </button>
        ))}
      </div>
    </div>
  );
}

function StepSkinTone({ data, setData }) {
  return (
    <div data-testid="step-skin-tone">
      <p className="text-xs uppercase tracking-[0.2em] text-gold font-medium mb-2">Your shade</p>
      <h1 className="font-serif text-4xl text-cocoa mb-2">Find your tone</h1>
      <p className="text-cocoa/60 font-light mb-7">
        Tap the swatch that best matches your skin.
      </p>
      <div className="grid grid-cols-4 gap-3">
        {SKIN_TONES.map((t) => (
          <button
            key={t.name}
            onClick={() => setData((d) => ({ ...d, skin_tone: t.name }))}
            data-testid={`skin-tone-${t.name.toLowerCase().replace(/\s+/g, "-")}`}
            className="flex flex-col items-center gap-1.5"
          >
            <div
              className={`w-14 h-14 rounded-full transition-all ${
                data.skin_tone === t.name
                  ? "ring-2 ring-gold ring-offset-4 ring-offset-cream scale-110"
                  : "ring-1 ring-cocoa/10 hover:scale-105"
              }`}
              style={{ background: t.hex }}
            />
            <span className="text-[10px] text-cocoa/70 font-light text-center leading-tight">{t.name}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function StepHairType({ data, setData }) {
  return (
    <div data-testid="step-hair-type">
      <p className="text-xs uppercase tracking-[0.2em] text-gold font-medium mb-2">Your texture</p>
      <h1 className="font-serif text-4xl text-cocoa mb-7">Your hair type?</h1>
      <div className="grid grid-cols-2 gap-3">
        {HAIR_TYPES.map((h) => (
          <button
            key={h}
            onClick={() => setData((d) => ({ ...d, hair_type: h }))}
            data-testid={`hair-type-${h.toLowerCase().replace(/\s+/g, "-")}`}
            className={`rounded-[20px] px-4 py-5 text-sm text-center font-medium transition-all ${
              data.hair_type === h
                ? "bg-rose text-white shadow-rose"
                : "bg-white text-cocoa shadow-card hover:bg-rose/5"
            }`}
          >
            {h}
          </button>
        ))}
      </div>
    </div>
  );
}

function StepConcerns({ data, toggle }) {
  return (
    <div data-testid="step-concerns">
      <p className="text-xs uppercase tracking-[0.2em] text-gold font-medium mb-2">Concerns</p>
      <h1 className="font-serif text-4xl text-cocoa mb-2">What are you working on?</h1>
      <p className="text-cocoa/60 font-light mb-6">Tap all that apply.</p>

      <p className="text-xs uppercase tracking-wider text-cocoa/50 mb-2">Skin</p>
      <div className="flex flex-wrap gap-2 mb-5">
        {CONCERNS.skin.map((c) => {
          const key = `skin:${c}`;
          return (
            <Chip
              key={key}
              label={c}
              selected={data.concerns.includes(key)}
              onClick={() => toggle("concerns", key)}
              testId={`concern-skin-${c.toLowerCase().replace(/\s+/g, "-")}`}
            />
          );
        })}
      </div>
      <p className="text-xs uppercase tracking-wider text-cocoa/50 mb-2">Hair</p>
      <div className="flex flex-wrap gap-2">
        {CONCERNS.hair.map((c) => {
          const key = `hair:${c}`;
          return (
            <Chip
              key={key}
              label={c}
              selected={data.concerns.includes(key)}
              onClick={() => toggle("concerns", key)}
              testId={`concern-hair-${c.toLowerCase().replace(/\s+/g, "-")}`}
            />
          );
        })}
      </div>
    </div>
  );
}

function StepGoals({ data, toggle }) {
  return (
    <div data-testid="step-goals">
      <p className="text-xs uppercase tracking-[0.2em] text-gold font-medium mb-2">Goals</p>
      <h1 className="font-serif text-4xl text-cocoa mb-2">What's your dream glow?</h1>
      <p className="text-cocoa/60 font-light mb-6">Pick everything you want to achieve.</p>
      <div className="flex flex-wrap gap-2">
        {GOALS.map((g) => (
          <Chip
            key={g}
            label={g}
            selected={data.goals.includes(g)}
            onClick={() => toggle("goals", g)}
            testId={`goal-${g.toLowerCase().replace(/\s+/g, "-")}`}
          />
        ))}
      </div>
    </div>
  );
}

function Chip({ label, selected, onClick, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`px-4 py-2.5 rounded-full text-sm font-medium transition-all border ${
        selected
          ? "bg-rose text-white border-rose shadow-rose"
          : "bg-white text-cocoa border-cocoa/10 hover:border-rose/40"
      }`}
    >
      {label}
    </button>
  );
}
